export interface DeviceMonitorSummary {
  id: number;
  key: string;
  label: string;
  kind?: string;
  display_order?: number;
  include_in_parent_selection?: boolean;
  overlaps_with?: string | null;
  status?: string;
}

export interface InstanceWithTemplateForHierarchy {
  id: number;
  name: string;
  description?: string;
  location_name?: string | null;
  consumer_name?: string | null;
  status: string;
  template_id: number;
  agent_id?: number | null;
  owner_group_id?: number | null;
  owner_group_name?: string | null;
  template_name: string;
  template_description?: string;
  category_id?: number;
  category_name?: string;
  monitors?: DeviceMonitorSummary[];
}

export type DeviceHierarchyNode = {
  label: string;
  id: string;
  db_id: number | string;
  node_type: "category" | "template" | "instance" | "monitor";
  children: DeviceHierarchyNode[];
  template_id?: string;
  template_name?: string;
  category_id?: string;
  instance_id?: number;
  monitor_key?: string;
  monitor_kind?: string;
  location_name?: string | null;
  consumer_name?: string | null;
  status?: string;
  agent_id?: number;
  owner_group_id?: number | null;
  owner_group_name?: string | null;
  secondary_label?: string;
};

export type DeviceReportSelection =
  | { kind: "instance"; instanceId: number }
  | { kind: "monitor"; monitorId: number };

export type ResolvedMeasurementSource =
  | {
      source_type: "instance";
      instance_id: number;
      label: string;
    }
  | {
      source_type: "monitor";
      monitor_id: number;
      instance_id: number;
      monitor_key: string;
      label: string;
    };

export interface DeviceReportSelectionIssue {
  code: string;
  message: string;
  sourceKeys?: string[];
}

export interface ResolvedDeviceReportSelection {
  sources: ResolvedMeasurementSource[];
  issues: DeviceReportSelectionIssue[];
}

export const formatAgentTreeDeviceLabel = (
  device: Pick<InstanceWithTemplateForHierarchy, "name">
) => device.name;

export const isLegacyTapoPlugPseudoDevice = (
  device: Pick<
    InstanceWithTemplateForHierarchy,
    "name" | "template_name" | "category_name" | "monitors"
  >
) => {
  const deviceName = String(device.name || "");
  const productName = String(device.template_name || "");
  const categoryName = String(device.category_name || "");
  const monitorCount = device.monitors?.length ?? 0;

  return (
    monitorCount === 0 &&
    /^Tapo P304M \d+ Plug \d+$/i.test(deviceName.trim()) &&
    /^Tapo P304M #\d+$/i.test(productName.trim()) &&
    categoryName.trim().toLowerCase() === "smart plug"
  );
};

const formatDeviceOwnerLabel = (
  device: Pick<InstanceWithTemplateForHierarchy, "owner_group_id" | "owner_group_name">
) => {
  if (device.owner_group_name) {
    return `Owner: ${device.owner_group_name}`;
  }
  if (device.owner_group_id !== null && device.owner_group_id !== undefined) {
    return `Owner Group ${device.owner_group_id}`;
  }
  return "Owner: Unassigned";
};

const getVisibleMonitorChildren = (device: InstanceWithTemplateForHierarchy): DeviceMonitorSummary[] => {
  const monitors = [...(device.monitors ?? [])].sort((a, b) => {
    const orderDelta = (a.display_order ?? 0) - (b.display_order ?? 0);
    if (orderDelta !== 0) return orderDelta;
    return a.label.localeCompare(b.label);
  });

  if (monitors.length <= 1) {
    return [];
  }

  return monitors;
};

export const buildMonitorAwareHierarchyFromDevices = (
  devices: InstanceWithTemplateForHierarchy[]
): DeviceHierarchyNode[] => {
  const categories = new Map<number, DeviceHierarchyNode>();

  devices.filter((device) => !isLegacyTapoPlugPseudoDevice(device)).forEach((device) => {
    const categoryId = Number(device.category_id);
    const templateId = Number(device.template_id);

    if (!categories.has(categoryId)) {
      categories.set(categoryId, {
        label: device.category_name || `Category ${categoryId}`,
        id: `c-${categoryId}`,
        db_id: categoryId,
        node_type: "category",
        children: [],
        status: "online",
      });
    }

    const categoryNode = categories.get(categoryId)!;
    let templateNode = categoryNode.children.find((child) => child.id === `t-${templateId}`);
    if (!templateNode) {
      templateNode = {
        label: device.template_name,
        id: `t-${templateId}`,
        db_id: templateId,
        node_type: "template",
        children: [],
        category_id: `c-${categoryId}`,
        status: "online",
      };
      categoryNode.children.push(templateNode);
    }

    const visibleMonitors = getVisibleMonitorChildren(device);
    templateNode.children.push({
      label: device.name,
      id: `i-${device.id}`,
      db_id: device.id,
      node_type: "instance",
      children: visibleMonitors.map((monitor) => ({
        label: monitor.label,
        id: `m-${monitor.id}`,
        db_id: monitor.id,
        node_type: "monitor",
        children: [],
        template_id: `t-${templateId}`,
        template_name: device.template_name,
        instance_id: device.id,
        monitor_key: monitor.key,
        monitor_kind: monitor.kind,
        status: monitor.status ?? device.status,
      })),
      template_id: `t-${templateId}`,
      template_name: device.template_name,
      status: device.status,
      agent_id: device.agent_id ?? undefined,
      owner_group_id: device.owner_group_id ?? null,
      owner_group_name: device.owner_group_name ?? null,
      secondary_label: formatDeviceOwnerLabel(device),
    });
  });

  return [...categories.values()].sort((a, b) => a.label.localeCompare(b.label)).map((category) => ({
    ...category,
    children: [...category.children]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((template) => ({
        ...template,
        children: [...template.children].sort((a, b) =>
          formatAgentTreeDeviceLabel({
            name: a.label,
          }).localeCompare(
            formatAgentTreeDeviceLabel({
              name: b.label,
            })
          )
        ),
      })),
  }));
};

export const resolveDeviceReportSelectionSources = (
  devices: InstanceWithTemplateForHierarchy[],
  selections: DeviceReportSelection[]
): ResolvedDeviceReportSelection => {
  const deviceById = new Map(devices.map((device) => [device.id, device]));
  const monitorById = new Map<number, { device: InstanceWithTemplateForHierarchy; monitor: DeviceMonitorSummary }>();
  devices.forEach((device) => {
    (device.monitors ?? []).forEach((monitor) => {
      monitorById.set(monitor.id, { device, monitor });
    });
  });

  const sources: ResolvedMeasurementSource[] = [];
  const issues: DeviceReportSelectionIssue[] = [];

  selections.forEach((selection) => {
    if (selection.kind === "instance") {
      const device = deviceById.get(selection.instanceId);
      if (!device) {
        issues.push({
          code: "unknown_instance",
          message: `Selected device ${selection.instanceId} was not found.`,
        });
        return;
      }

      const monitors = [...(device.monitors ?? [])].sort(sortMonitors);
      if (monitors.length <= 1) {
        const monitor = monitors[0];
        if (monitor) {
          sources.push(monitorSource(device, monitor));
          return;
        }
        sources.push({
          source_type: "instance",
          instance_id: device.id,
          label: formatAgentTreeDeviceLabel(device),
        });
        return;
      }

      monitors
        .filter((monitor) => monitor.include_in_parent_selection !== false)
        .forEach((monitor) => sources.push(monitorSource(device, monitor)));
      return;
    }

    const match = monitorById.get(selection.monitorId);
    if (!match) {
      issues.push({
        code: "unknown_monitor",
        message: `Selected monitor ${selection.monitorId} was not found.`,
      });
      return;
    }
    sources.push(monitorSource(match.device, match.monitor));
  });

  const dedupedSources = dedupeSources(sources);
  return {
    sources: dedupedSources,
    issues: [...issues, ...detectMonitorOverlaps(dedupedSources, monitorById)],
  };
};

const sortMonitors = (a: DeviceMonitorSummary, b: DeviceMonitorSummary) => {
  const orderDelta = (a.display_order ?? 0) - (b.display_order ?? 0);
  if (orderDelta !== 0) return orderDelta;
  return a.label.localeCompare(b.label);
};

const monitorSource = (
  device: InstanceWithTemplateForHierarchy,
  monitor: DeviceMonitorSummary
): ResolvedMeasurementSource => ({
  source_type: "monitor",
  monitor_id: monitor.id,
  instance_id: device.id,
  monitor_key: monitor.key,
  label: `${formatAgentTreeDeviceLabel(device)} / ${monitor.label}`,
});

const sourceKey = (source: ResolvedMeasurementSource): string =>
  source.source_type === "monitor"
    ? `monitor:${source.monitor_id}`
    : `instance:${source.instance_id}`;

const dedupeSources = (
  sources: ResolvedMeasurementSource[]
): ResolvedMeasurementSource[] => {
  const seen = new Set<string>();
  const deduped: ResolvedMeasurementSource[] = [];
  sources.forEach((source) => {
    const key = sourceKey(source);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    deduped.push(source);
  });
  return deduped;
};

const detectMonitorOverlaps = (
  sources: ResolvedMeasurementSource[],
  monitorById: Map<number, { device: InstanceWithTemplateForHierarchy; monitor: DeviceMonitorSummary }>
): DeviceReportSelectionIssue[] => {
  const selectedMonitorKeysByInstance = new Map<number, Set<string>>();
  sources.forEach((source) => {
    if (source.source_type !== "monitor") {
      return;
    }
    const bucket = selectedMonitorKeysByInstance.get(source.instance_id) ?? new Set<string>();
    bucket.add(source.monitor_key);
    selectedMonitorKeysByInstance.set(source.instance_id, bucket);
  });

  const issues: DeviceReportSelectionIssue[] = [];
  sources.forEach((source) => {
    if (source.source_type !== "monitor") {
      return;
    }
    const monitor = monitorById.get(source.monitor_id)?.monitor;
    const selectedKeys = selectedMonitorKeysByInstance.get(source.instance_id);
    if (!monitor || !selectedKeys) {
      return;
    }
    parseOverlapKeys(monitor.overlaps_with)
      .filter((overlapKey) => selectedKeys.has(overlapKey))
      .forEach((overlapKey) => {
        const sourceKeys = [`${source.instance_id}/${source.monitor_key}`, `${source.instance_id}/${overlapKey}`]
          .sort();
        if (
          issues.some((issue) => issue.sourceKeys?.join("|") === sourceKeys.join("|"))
        ) {
          return;
        }
        issues.push({
          code: "overlapping_monitor_selection",
          message: "Selected monitors overlap and may double-count measurements.",
          sourceKeys,
        });
      });
  });
  return issues;
};

const parseOverlapKeys = (overlapsWith?: string | null): string[] => {
  if (!overlapsWith) {
    return [];
  }
  const trimmed = overlapsWith.trim();
  if (!trimmed) {
    return [];
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.filter((value): value is string => typeof value === "string");
    }
  } catch {
    // Fall back to comma-separated legacy text.
  }
  return trimmed
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
};
